import axios from 'axios';

import store from '../../redux/store';
import { EXTERNAL_API, PLUGIN_URL, WORDPRESS_BASE_URL } from './globalConfig';
import {
  getHeaderValue,
  isInvalidOrRevokedCredentialError,
  isTransientAuthFailure,
} from '../../services/webAPI/authError';

import qs from 'qs';
import { logDebug } from './globalFunctions';
import { managerApiTokenSelector } from '../../redux/selectors/desktopApp.selector';

const storeInstance = store();

/**
 * @typedef {import('axios').InternalAxiosRequestConfig & {
 *   _guestRetried?: boolean;
 *   _guestMint?: boolean;
 *   _authKind?: 'authenticated'|'guest'|'anonymous';
 *   _authRefresh?: boolean;
 *   _authRetried?: boolean;
 * }} AuthRequestConfig
 */

/**
 * GIMmanager `isProduction` follows Vite *mode* (which `.env` file was loaded),
 * not `import.meta.env.PROD`. `vite build --mode development` is still a
 * production bundle (`PROD=true`) while targeting the test API.
 * Axios omits boolean false headers; GIMmanager JWT requires the literal strings.
 * @param {string} [mode] Vite mode (`development`, `production`, `localapi`, …)
 * @returns {'true'|'false'}
 */
export const managerIsProductionHeader = (mode = import.meta.env.MODE) =>
  mode === 'production' ? 'true' : 'false';

const isProductionHeader = managerIsProductionHeader();

logDebug('Axios production mode', isProductionHeader === 'true');
const CancelToken = axios.CancelToken;

/**
 * @param {unknown} error Axios-like error
 * @param {string} accessToken current interactive access token
 * @returns {boolean}
 */
const shouldRetryGuest401 = (error, accessToken = '') => {
  const authError =
    /** @type {{response?: {status?: number}; config?: AuthRequestConfig}} */ (
      error
    );
  const config = authError?.config;
  const sentAuth = getHeaderValue(config?.headers, 'Authorization');
  const isExplicitGuestRequest = config?._authKind === 'guest';
  const isExplicitAnonymousRequest =
    config?._authKind === 'anonymous' || !sentAuth;
  const isProtectedRequest =
    config?._authKind === 'authenticated' ||
    (Boolean(sentAuth) && !isExplicitGuestRequest);

  return Boolean(
    authError?.response?.status === 401 &&
      config &&
      !config._guestRetried &&
      !config._guestMint &&
      !accessToken &&
      !isProtectedRequest &&
      (isExplicitGuestRequest || isExplicitAnonymousRequest)
  );
};

/**
 * Removes a request Authorization header without exposing refresh credentials.
 * @param {unknown} headers request headers
 * @returns {void}
 */
const removeAuthorizationHeader = (headers) => {
  if (!headers || typeof headers !== 'object') return;
  const headerCollection = /** @type {{delete?: (header: string) => void}} */ (
    headers
  );
  if (typeof headerCollection.delete === 'function') {
    headerCollection.delete('Authorization');
    return;
  }
  const plainHeaders = /** @type {Record<string, unknown>} */ (headers);
  delete plainHeaders.Authorization;
  delete plainHeaders.authorization;
};

/**
 * Logs only non-sensitive Axios failure metadata.
 * @param {string} label diagnostic label
 * @param {unknown} error Axios-like error
 * @returns {void}
 */
const logAxiosDiagnostic = (label, error) => {
  const axiosError =
    /** @type {{message?: string; response?: {status?: number}}} */ (error);
  console.error(
    label,
    axiosError?.message ?? 'Request failed',
    axiosError?.response?.status
  );
};

/**
 *
 */
export const cancelSource = CancelToken.source();

const requestInterceptor = [
  (request) => {
    request.cancelToken = cancelSource.token;
    return request;
  },
  (error) => logAxiosDiagnostic('Axios request failed', error),
];

/**
 * Sets an Authorization header on AxiosHeaders or a plain object.
 * @param {unknown} headers request headers
 * @param {string} value bearer authorization value
 * @returns {void}
 */
const setAuthorizationHeader = (headers, value) => {
  if (!headers || typeof headers !== 'object') return;
  const headerCollection =
    /** @type {{set?: (header: string, value: string) => void}} */ (headers);
  if (typeof headerCollection.set === 'function') {
    headerCollection.set('Authorization', value);
    return;
  }
  /** @type {Record<string, unknown>} */ (headers).Authorization = value;
};

/**
 * Applies current browser auth to remote requests.
 * @param {import('axios').InternalAxiosRequestConfig & {
 *   _authKind?: 'authenticated'|'guest'|'anonymous';
 *   _authRefresh?: boolean;
 * }} request remote request
 * @returns {import('axios').InternalAxiosRequestConfig}
 */
const externalRequestAuthInterceptor = (request) => {
  if (request._authRefresh) {
    removeAuthorizationHeader(request.headers);
    request._authKind = 'anonymous';
    return request;
  }

  const state = storeInstance.getState();
  const accessToken = state?.auth?.authToken || '';
  const guestToken = state?.auth?.guestToken || '';
  const existingAuthKind = request._authKind;

  // Preserve the classification made when this request was created. In
  // particular, an authenticated request must not become a guest request
  // when logout clears the access token before a retry enters this interceptor.
  if (existingAuthKind === 'authenticated') {
    if (accessToken) {
      setAuthorizationHeader(request.headers, `Bearer ${accessToken}`);
    }
    return request;
  }
  if (existingAuthKind === 'guest') {
    if (guestToken && !accessToken) {
      setAuthorizationHeader(request.headers, `Bearer ${guestToken}`);
    }
    return request;
  }
  if (existingAuthKind === 'anonymous') {
    removeAuthorizationHeader(request.headers);
    return request;
  }

  const sentAuth = getHeaderValue(request.headers, 'Authorization');
  if (sentAuth) {
    const accessAuthorization = accessToken
      ? `Bearer ${accessToken}`
      : undefined;
    const guestAuthorization =
      !accessToken && guestToken ? `Bearer ${guestToken}` : undefined;

    // Axios defaults may already contain the token. Mark it once so later
    // retries retain its original auth kind even if Redux state changes.
    request._authKind =
      sentAuth === accessAuthorization
        ? 'authenticated'
        : sentAuth === guestAuthorization
          ? 'guest'
          : 'authenticated';
    return request;
  }

  if (accessToken) {
    setAuthorizationHeader(request.headers, `Bearer ${accessToken}`);
    request._authKind = 'authenticated';
  } else if (guestToken) {
    setAuthorizationHeader(request.headers, `Bearer ${guestToken}`);
    request._authKind = 'guest';
  } else {
    removeAuthorizationHeader(request.headers);
    request._authKind = 'anonymous';
  }

  return request;
};

const responseInterceptor = [
  (response) => {
    return response;
  },
  (error) => {
    logAxiosDiagnostic('Axios response failed', error);
    if (error?.response?.data?.Message) {
      //toast.error(error.response.data.Message || 'Oops! something went wrong');
    } else if (error.message?.toLowerCase() === 'network error') {
      // toast.error('Oops! something went wrong');
    } else if (error.message !== 'Request failed with status code 404') {
      // toast.error(error.message || 'Oops! something went wrong');
    }
    return Promise.reject(error);
  },
];

const responseLocalInterceptor = [
  (response) => {
    return response;
  },
  (error) => {
    logAxiosDiagnostic('Local API response failed', error);
    if (error?.response?.data?.Message) {
      // toast.error(error.response.data.Message || 'Oops! something went wrong');
    } else if (error.message?.toLowerCase() === 'network error') {
      // toast.error('Please install or restart GIMmanager');
    } else if (error.message !== 'Request failed with status code 404') {
      // toast.error(error.message || 'Oops! something went wrong');
    }
    return Promise.reject(error);
  },
];

const externalApi = axios.create({
  baseURL: EXTERNAL_API,
  paramsSerializer: (params) => qs.stringify(params, { indices: false }),
});

const localApi = axios.create({
  baseURL: PLUGIN_URL,
  headers: {
    isProduction: isProductionHeader,
  },
});

/**
 * Local manager requests need the environment header as a string and the
 * in-memory capability token. Browser access tokens are a different audience.
 * @param {import('axios').InternalAxiosRequestConfig} request local request
 * @returns {import('axios').InternalAxiosRequestConfig}
 */
const localRequestAuthInterceptor = (request) => {
  if (request.headers && typeof request.headers.set === 'function') {
    request.headers.set('isProduction', isProductionHeader);
  } else if (request.headers) {
    request.headers.isProduction = isProductionHeader;
  }

  const managerApiToken = managerApiTokenSelector(storeInstance.getState());
  if (typeof managerApiToken === 'string' && managerApiToken.length > 0) {
    setAuthorizationHeader(request.headers, `Bearer ${managerApiToken}`);
  }
  return request;
};

const wpApi = axios.create({
  baseURL: WORDPRESS_BASE_URL,
});

/**
 * Refreshes and retries one authenticated request after an expired token.
 * @param {import('axios').AxiosError} error failed remote request
 * @returns {Promise<unknown>}
 */
export const responseExternalErrorInterceptor = async (error) => {
  // Guest token 401 recovery: when an anonymous request used a guest token
  // (or had no Authorization at all because the boot mint had not resolved
  // yet) and the server rejected it (e.g. expired TTL), mint a fresh guest
  // token once and retry the same request. No redirects/logouts here -
  // public pages just degrade gracefully when the retry also fails.
  const config = /** @type {AuthRequestConfig|undefined} */ (error?.config);
  const state = storeInstance.getState();
  const accessToken = state?.auth?.authToken || '';
  const sentAuth = getHeaderValue(config?.headers, 'Authorization');
  const isGuest401 = shouldRetryGuest401(error, accessToken);

  if (isGuest401) {
    config._guestRetried = true;
    // Dynamic import breaks axiosConfig -> auth.actions -> axiosConfig cycle
    // and defers auth action loading until interceptor execution.
    const { getGuestToken } = await import(
      '../../redux/actions/auth/auth.actions'
    );
    await storeInstance.dispatch(getGuestToken());
    // The mint can return a token it refused to store once live auth exists.
    // Retry only with a bearer that is in memory after the mint resolves.
    const authAfterMint = storeInstance.getState()?.auth;
    const accessTokenAfterMint = authAfterMint?.authToken || '';
    const guestTokenAfterMint = authAfterMint?.guestToken || '';
    if (accessTokenAfterMint) {
      config._authKind = 'authenticated';
      setAuthorizationHeader(config.headers, `Bearer ${accessTokenAfterMint}`);
      return externalApi(config);
    }
    if (guestTokenAfterMint) {
      config._authKind = 'guest';
      setAuthorizationHeader(config.headers, `Bearer ${guestTokenAfterMint}`);
      return externalApi(config);
    }
  }

  const isAuthenticatedRequest =
    config?._authKind === 'authenticated' ||
    sentAuth === `Bearer ${accessToken}`;
  const shouldRefreshAccessToken =
    isAuthenticatedRequest &&
    !config?._authRefresh &&
    !config?._authRetried &&
    isInvalidOrRevokedCredentialError(error);

  if (shouldRefreshAccessToken) {
    config._authRetried = true;
    try {
      // Dynamic import breaks axiosConfig -> auth.actions -> axiosConfig cycle.
      const { refreshAccessToken } = await import(
        '../../redux/actions/auth/auth.actions'
      );
      const tokenResult = await storeInstance.dispatch(
        refreshAccessToken({ silent: true })
      );
      setAuthorizationHeader(
        config.headers,
        `Bearer ${tokenResult.accessToken}`
      );
      return externalApi(config);
    } catch (refreshError) {
      // Refresh thunk classifies definitive failures and preserves auth for
      // transient failures. Never retry the refresh request itself.
      return Promise.reject(refreshError);
    }
  }

  const isAnonymous401 =
    error?.response?.status === 401 &&
    !accessToken &&
    (config?._authKind === 'anonymous' || !sentAuth);
  if (isAnonymous401) {
    // Expected for anonymous browsing on user-only endpoints; no toast.
    logDebug('externalApi 401 for anonymous request', error?.message);
  } else if (isTransientAuthFailure(error)) {
    logAxiosDiagnostic(
      'externalApi auth request temporarily unavailable',
      error
    );
  } else {
    logAxiosDiagnostic('External API response failed', error);
    if (error?.response?.data?.Message) {
      //toast.error(error.response.data.Message || 'Oops! something went wrong');
    } else if (error.message?.toLowerCase() === 'network error') {
      // toast.error('Oops! something went wrong');
    } else if (error.message !== 'Request failed with status code 404') {
      // toast.error(error.message || 'Oops! something went wrong');
    }
  }
  return Promise.reject(error);
};

const responseExternalInterceptor = [
  (response) => {
    return response;
  },
  responseExternalErrorInterceptor,
];

const listner = () => {
  const { userLanguage } = storeInstance.getState().user;
  const managerApiToken = managerApiTokenSelector(storeInstance.getState());
  const { authToken, guestToken } = storeInstance.getState().auth;
  localApi.defaults.headers.common.Authorization = managerApiToken
    ? `Bearer ${managerApiToken}`
    : null;
  externalApi.defaults.headers.common.Authorization = authToken
    ? `Bearer ${authToken}`
    : guestToken
      ? `Bearer ${guestToken}`
      : null;
  externalApi.defaults.headers.common.Language = userLanguage ?? 'en';
};

storeInstance.subscribe(listner);

externalApi.interceptors.request.use(...requestInterceptor);
externalApi.interceptors.request.use(externalRequestAuthInterceptor);
externalApi.interceptors.response.use(...responseExternalInterceptor);

localApi.interceptors.request.use(...requestInterceptor);
localApi.interceptors.request.use(localRequestAuthInterceptor);
localApi.interceptors.response.use(...responseLocalInterceptor);

wpApi.interceptors.request.use(...requestInterceptor);
wpApi.interceptors.response.use(...responseInterceptor);

/**
 *
 */
export { localApi, externalApi, wpApi };
